import { useEffect, useState } from 'react';

import { cn } from '../lib/utils';

import { useChipSize } from '../chip/chip-context';
import { initialLength, initialTextSize } from '../chip/tokens';

const getInitial = (name: string, length: 1 | 2): string => {
  const words = name.split(/\s+/).filter(Boolean);
  return words
    .slice(0, length)
    .map((word) => Array.from(word)[0] ?? '')
    .join('')
    .toUpperCase();
};

type CommonProps = {
  className?: string;
};

type InitialProps = CommonProps & {
  type: 'initial';
  /** Source of the initials. Required. */
  name: string;
};

type ImageProps = CommonProps & {
  type: 'image';
  /** Resolved URL. The component does **not** resolve API-signed URLs —
   *  use `useResolvedMediaUrl` (or equivalent) at the call site and pass
   *  the resolved string. */
  src: string;
  /** Optional fallback source. When provided, a load failure on `src`
   *  renders initials from `name` instead of the broken-image glyph.
   *  Without `name`, the silhouette is used as the fallback. */
  name?: string;
};

type UnknownProps = CommonProps & {
  type: 'unknown';
};

type Props = InitialProps | ImageProps | UnknownProps;

const UnknownSilhouette = ({ className }: { className?: string }) => (
  // Inline SVG so the silhouette inherits `currentColor` from the chip's
  // text color — one shape, both color variants.
  <svg
    viewBox="0 0 32 32"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('size-full', className)}
    aria-hidden="true"
  >
    <path d="M16.0005 20C20.0006 20 23.5428 21.6214 25.7315 24.1153C26.6024 25.1077 26.3862 26.6394 25.2089 27.2373C24.6308 27.5308 23.8316 27.753 22.3449 27.874C20.8228 27.9979 18.8595 28 16.0005 28C13.1415 28 11.1781 27.9979 9.65613 27.874C8.1694 27.753 7.37016 27.5308 6.7921 27.2373C5.61434 26.6392 5.39765 25.1074 6.26905 24.1146C8.4579 21.621 12.0005 20 16.0005 20Z" />
    <path d="M16.0838 6C19.1158 6 21.5189 8.65383 21.5189 11.8477C21.5186 15.2501 19.1403 17.8536 16.0838 17.8291C13.0275 17.8045 10.6489 15.2501 10.6487 11.8477C10.624 8.65388 13.0519 6.00007 16.0838 6Z" />
  </svg>
);

/** Identity content for a `<Chip>`: initials, image, or unknown silhouette.
 *  Strictly the "represents a person/entity" case — for country flags use
 *  `<Flag>`, for system icons use `<Glyph>`. Sizing comes from the
 *  parent Chip via context.
 *
 *  Image fallback: when `type="image"` and the resolved URL fails to load
 *  (404, CORS, expired Google OAuth thumbnail, etc.), the component drops
 *  to initials from `name` if provided, otherwise the silhouette. A bare
 *  broken-image glyph is never shown. */
export const Avatar = (props: Props) => {
  const size = useChipSize();
  const { type, className } = props;
  const [imageFailed, setImageFailed] = useState(false);

  // Reset on src change so a fresh URL gets a fresh attempt.
  const srcKey = type === 'image' ? props.src : null;
  useEffect(() => {
    setImageFailed(false);
  }, [srcKey]);

  // text-box trims the line-box to cap-baseline so flex-center aligns on
  // the visible glyph, not the em-square. Without this, capital initials
  // drift toward the top of the tile.
  const renderInitials = (name: string) => (
    <span
      className={cn(
        'select-none font-semibold [text-box:trim-both_cap_alphabetic]',
        initialTextSize[size],
        className,
      )}
    >
      {getInitial(name, initialLength[size])}
    </span>
  );

  switch (type) {
    case 'image':
      if (!imageFailed) {
        return (
          <img
            src={props.src}
            alt=""
            className={cn('size-full object-cover', className)}
            onError={() => setImageFailed(true)}
          />
        );
      }
      return props.name ? renderInitials(props.name) : <UnknownSilhouette className={className} />;

    case 'initial':
      return renderInitials(props.name);

    case 'unknown':
      return <UnknownSilhouette className={className} />;
  }
};

export type { Props as AvatarProps };
